import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  AlertCircle,
  ArrowLeft,
  Battery,
  Car,
  CheckCircle2,
  Clock,
  CreditCard,
  History,
  Loader2,
  Navigation,
  Play,
  RefreshCw,
  Square,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { PullToRefresh } from '@/components/pull-to-refresh'
import { TimeAgo } from '@/components/time-ago'
import { LoadingSpinner } from '@/components/loading-spinner'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { getApiErrorMessage, localizeDigits } from '@/lib/utils'
import { useAccount, useAccounts } from '@/lib/use-accounts'
import { useCountdown, useCountup } from '@/lib/use-countdown'
import { useAuth } from '@/lib/auth-oidc'
import {
  useCancelRental,
  useContinueRental,
  useCurrentRental,
  useEndTrip,
  useExtendRental,
  useFuelCard,
  useStartTrip,
  useTripHistory,
} from '@/lib/api'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  RentalLocationMap,
  openDirections,
} from '@/components/rental-location-map'

export const Route = createFileRoute('/profile/$accountId')({
  component: AccountDetailsPage,
})

// Countdown timer component
function Countdown({ targetDate }: { targetDate: Date | null | undefined }) {
  const countdown = useCountdown(targetDate)
  return <>{countdown}</>
}

// Trip timer component - counts up from start time
function TripTimer({ startDate }: { startDate: Date | null | undefined }) {
  const elapsed = useCountup(startDate)
  if (!elapsed) return null
  return <>{elapsed}</>
}

// Elapsed timer that includes days (e.g. "2d 5h 30m 12s")
function ElapsedSince({ date }: { date: Date }) {
  const [text, setText] = useState('')

  useEffect(() => {
    const update = () => {
      const ms = Date.now() - date.getTime()
      if (ms < 0) {
        setText('0s')
        return
      }
      const s = Math.floor(ms / 1000)
      const d = Math.floor(s / 86400)
      const h = Math.floor((s % 86400) / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = s % 60
      const parts: Array<string> = []
      if (d > 0) parts.push(`${d}d`)
      if (h > 0) parts.push(`${h}h`)
      if (m > 0) parts.push(`${m}m`)
      parts.push(`${sec}s`)
      setText(localizeDigits(parts.join(' ')))
    }
    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [date])

  return <>{text}</>
}

function AccountDetailsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { accountId } = Route.useParams()
  const { isAuthenticated, isLoading: isAuthLoading, login } = useAuth()

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS (Rules of Hooks)
  // Fetch account data - single source of truth
  const {
    account,
    accountStatus, // Raw API response from same cache as accounts list
    isLoading: accountLoading,
    isNotFound,
    refresh: refreshAccount,
    dataUpdatedAt,
  } = useAccount(accountId)
  const {
    data: rental,
    error: rentalError,
    isError: isRentalError,
  } = useCurrentRental(accountId)

  // Only fetch fuel card when there's an active trip
  const isInTrip = account?.status === 'ongoing-trip'
  const { data: fuelCard } = useFuelCard(accountId, isInTrip)

  // Trip history with page accumulation
  const [tripHistoryPage, setTripHistoryPage] = useState(1)
  const {
    data: tripHistoryData,
    isLoading: isTripHistoryLoading,
    isFetching: isTripHistoryFetching,
  } = useTripHistory(accountId, tripHistoryPage)
  const [allTrips, setAllTrips] = useState<
    NonNullable<typeof tripHistoryData>['rentals']
  >([])
  const [hasMoreTrips, setHasMoreTrips] = useState(false)

  useEffect(() => {
    if (tripHistoryData) {
      setAllTrips((prev) =>
        tripHistoryPage === 1
          ? tripHistoryData.rentals
          : [...prev, ...tripHistoryData.rentals],
      )
      setHasMoreTrips(tripHistoryData.has_next_page)
    }
  }, [tripHistoryData, tripHistoryPage])

  const loadMoreTrips = useCallback(() => {
    if (hasMoreTrips) {
      setTripHistoryPage((p) => p + 1)
    }
  }, [hasMoreTrips])

  // Get all accounts for the end trip & reserve dialog
  const { accounts } = useAccounts()

  // Mutations
  const startTripMutation = useStartTrip()
  const endTripMutation = useEndTrip()
  const cancelRentalMutation = useCancelRental()
  const extendRentalMutation = useExtendRental()
  const continueRentalMutation = useContinueRental()

  // Dialog states
  const [showStartTripDialog, setShowStartTripDialog] = useState(false)
  const [showEndTripDialog, setShowEndTripDialog] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showEndTripReserveDialog, setShowEndTripReserveDialog] =
    useState(false)

  const isLoading = accountLoading
  const isMutating =
    startTripMutation.isPending ||
    endTripMutation.isPending ||
    cancelRentalMutation.isPending ||
    extendRentalMutation.isPending ||
    continueRentalMutation.isPending

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      login()
    }
  }, [isAuthenticated, isAuthLoading, login])

  // CONDITIONAL RENDERING (after all hooks are called)
  // Show loading while checking authentication
  if (isAuthLoading) {
    return <LoadingSpinner variant="fullscreen" />
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null
  }

  const handleRefresh = () => {
    refreshAccount()
  }

  const handleStartTrip = async () => {
    try {
      await startTripMutation.mutateAsync({ accountName: accountId })
      setShowStartTripDialog(false)
      toast.success(t('profile.accounts.tripStarted'))
    } catch (error) {
      console.error('Failed to start trip:', error)
      const errorMsg = getApiErrorMessage(
        error,
        t('profile.accounts.actionFailed'),
      )
      toast.error(errorMsg)
    }
  }

  const handleEndTrip = async () => {
    try {
      await endTripMutation.mutateAsync({ accountName: accountId })
      setShowEndTripDialog(false)
      toast.success(t('profile.accounts.tripEnded'))
    } catch (error) {
      console.error('Failed to end trip:', error)
      const errorMsg = getApiErrorMessage(
        error,
        t('profile.accounts.actionFailed'),
      )
      toast.error(errorMsg)
    }
  }

  const handleCancelReservation = async () => {
    try {
      await cancelRentalMutation.mutateAsync({ accountName: accountId })
      setShowCancelDialog(false)
      toast.success(t('profile.accounts.reservationCancelled'))
    } catch (error) {
      console.error('Failed to cancel reservation:', error)
      const errorMsg = getApiErrorMessage(
        error,
        t('profile.accounts.actionFailed'),
      )
      toast.error(errorMsg)
    }
  }

  const handleExtendRental = async () => {
    try {
      await extendRentalMutation.mutateAsync({ accountName: accountId })
      toast.success(t('profile.accounts.rentalExtended'))
    } catch (error) {
      console.error('Failed to extend rental:', error)
      const errorMsg = getApiErrorMessage(
        error,
        t('profile.accounts.actionFailed'),
      )
      toast.error(errorMsg)
    }
  }

  const handleEndTripAndReserve = async (toAccountId: string) => {
    try {
      await continueRentalMutation.mutateAsync({
        endOnAccount: accountId,
        continueOnAccount: toAccountId,
      })
      setShowEndTripReserveDialog(false)
      toast.success(t('profile.accounts.endTripReserveSuccess'))
    } catch (error) {
      console.error('Continue rental failed:', error)
      const errorMsg = getApiErrorMessage(
        error,
        t('profile.accounts.actionFailed'),
      )
      toast.error(errorMsg)
    }
  }

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString)
    const formatted = new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date)
    return localizeDigits(formatted)
  }

  const formatTimeUntil = (date: Date): string => {
    const diffMs = date.getTime() - Date.now()
    if (diffMs <= 0) return localizeDigits('0m')
    const diffHours = Math.floor(diffMs / 3600000)
    const diffMins = Math.floor((diffMs % 3600000) / 60000)
    if (diffHours > 0) {
      return localizeDigits(`${diffHours}h ${diffMins}m`)
    }
    return localizeDigits(`${diffMins}m`)
  }

  const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object' && 'detail' in error) {
      return String(error.detail)
    }
    if (error instanceof Error) {
      return error.message
    }
    return t('common.error')
  }

  const getStatusBadge = () => {
    if (account?.status === 'ongoing-trip') {
      return (
        <Badge variant="default" className="bg-green-600">
          <Play className="h-3 w-3 me-1" />
          {t('profile.accounts.tripActive')}
        </Badge>
      )
    } else if (account?.status === 'reserved') {
      return (
        <Badge variant="default" className="bg-blue-600">
          <Clock className="h-3 w-3 me-1" />
          {t('profile.accounts.reserved')}
        </Badge>
      )
    } else if (accountStatus?.has_token) {
      // Use accountStatus directly from API for more reliable has_token value
      return (
        <Badge
          variant="outline"
          className="border-green-600 text-green-700 dark:text-green-400"
        >
          <CheckCircle2 className="h-3 w-3 me-1" />
          {t('profile.accounts.connected')}
        </Badge>
      )
    } else {
      return (
        <Badge
          variant="outline"
          className="border-red-600 text-red-700 dark:text-red-400"
        >
          <CheckCircle2 className="h-3 w-3 me-1" />
          {t('profile.accounts.disconnected')}
        </Badge>
      )
    }
  }

  return (
    <div className="fixed-fullscreen-safe fixed overflow-y-auto">
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => navigate({ to: '/profile' })}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-xl font-bold">
                  {account?.name || accountId}
                </h1>
                <p className="text-xs text-muted-foreground">
                  {account?.email}
                </p>
                {/* Data Age Indicator */}
                {dataUpdatedAt > 0 && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <Clock className="h-3 w-3" />
                    <span>
                      <TimeAgo
                        date={new Date(dataUpdatedAt)}
                        prefix={t('common.updated')}
                      />
                    </span>
                  </div>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
              />
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <PullToRefresh
        onRefresh={async () => {
          await refreshAccount()
        }}
        className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area"
      >
        <div className="space-y-4 pt-4">
          {/* Loading State */}
          {isLoading && <LoadingSpinner />}

          {/* Account Status Card */}
          {!isLoading && account && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    {t('profile.accountDetails.status')}
                  </CardTitle>
                  {getStatusBadge()}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Last Trip End */}
                {accountStatus?.last_end_trip && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>
                      {t('profile.accounts.lastTrip')}:{' '}
                      {formatDateTime(accountStatus.last_end_trip)}
                    </span>
                  </div>
                )}

                {/* Next Free Time */}
                {account.nextFree ? (
                  account.nextFree.hasSubscription ? (
                    account.nextFree.isFree ? (
                      <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>{t('profile.accountDetails.freeNow')}</span>
                      </div>
                    ) : account.nextFree.nextFreeTime ? (
                      <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                        <Clock className="h-4 w-4" />
                        <span>
                          {t('profile.accountDetails.freeIn', {
                            time: formatTimeUntil(
                              account.nextFree.nextFreeTime,
                            ),
                          })}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{t('profile.accountDetails.freeNotSoon')}</span>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{t('profile.accountDetails.freeNever')}</span>
                    </div>
                  )
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t('common.loading')}</span>
                  </div>
                )}

                {/* No Rental */}
                {account.status === 'idle' && (
                  <div className="text-sm text-muted-foreground">
                    {t('profile.accountDetails.noActiveRental')}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Rental Error State */}
          {isRentalError && account && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-destructive">
                  <AlertCircle className="h-5 w-5" />
                  {t('common.error')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {getErrorMessage(rentalError)}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Current Rental Card */}
          {rental && !isRentalError && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Car className="h-5 w-5" />
                    {t('profile.accountDetails.currentRental')}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Vehicle Info */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {rental.vehicle.model ||
                        `Vehicle #${localizeDigits(String(rental.vehicle.vehicleNb))}`}
                    </span>
                    <Badge variant="outline">
                      #{localizeDigits(String(rental.vehicle.vehicleNb))}
                    </Badge>
                  </div>

                  {/* Energy Level */}
                  {rental.vehicle.energyLevelPercentage != null && (
                    <div className="flex items-center gap-2 text-sm">
                      <Battery className="h-4 w-4" />
                      <span>
                        {localizeDigits(
                          `${rental.vehicle.energyLevelPercentage}%`,
                        )}
                      </span>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            rental.vehicle.energyLevelPercentage > 50
                              ? 'bg-green-500'
                              : rental.vehicle.energyLevelPercentage > 20
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                          }`}
                          style={{
                            width: `${rental.vehicle.energyLevelPercentage}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Location Map */}
                  {rental.vehicle.vehicleLocation && (
                    <div className="space-y-2">
                      <RentalLocationMap
                        carLocation={rental.vehicle.vehicleLocation}
                        carName={
                          rental.vehicle.model ||
                          `Vehicle #${rental.vehicle.vehicleNb}`
                        }
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() =>
                          openDirections(
                            rental.vehicle.vehicleLocation!.latitude,
                            rental.vehicle.vehicleLocation!.longitude,
                          )
                        }
                      >
                        <Navigation className="h-4 w-4 me-1" />
                        {t('profile.accountDetails.getDirections')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Rental Times */}
                <div className="space-y-2 pt-2 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {t('profile.accountDetails.reservedAt')}
                    </span>
                    <span>{formatDateTime(rental.reservedStartDate)}</span>
                  </div>
                  {rental.usedStartDate ? (
                    /* Ongoing trip - show trip duration counting up */
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t('profile.accountDetails.tripDuration')}
                      </span>
                      <span className="text-lg font-bold text-green-600 dark:text-green-400 font-mono tabular-nums">
                        <TripTimer startDate={new Date(rental.usedStartDate)} />
                      </span>
                    </div>
                  ) : (
                    /* Reservation - show expires in countdown */
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t('profile.accountDetails.expiresIn')}
                      </span>
                      <span>
                        <Countdown
                          targetDate={new Date(rental.reservedEndDate)}
                        />
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  {account?.status === 'reserved' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => setShowStartTripDialog(true)}
                        disabled={isMutating}
                      >
                        {startTripMutation.isPending ? (
                          <Loader2 className="h-4 w-4 me-1 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4 me-1" />
                        )}
                        {t('profile.accounts.startTrip')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleExtendRental}
                        disabled={isMutating}
                      >
                        {extendRentalMutation.isPending ? (
                          <Loader2 className="h-4 w-4 me-1 animate-spin" />
                        ) : (
                          <Clock className="h-4 w-4 me-1" />
                        )}
                        {t('profile.accountDetails.extend')}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setShowCancelDialog(true)}
                        disabled={isMutating}
                      >
                        {cancelRentalMutation.isPending ? (
                          <Loader2 className="h-4 w-4 me-1 animate-spin" />
                        ) : (
                          <X className="h-4 w-4 me-1" />
                        )}
                        {t('profile.accountDetails.cancel')}
                      </Button>
                    </>
                  )}
                  {account?.status === 'ongoing-trip' && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setShowEndTripDialog(true)}
                      disabled={isMutating}
                    >
                      {endTripMutation.isPending ? (
                        <Loader2 className="h-4 w-4 me-1 animate-spin" />
                      ) : (
                        <Square className="h-4 w-4 me-1" />
                      )}
                      {t('profile.accounts.endTrip')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fuel Card (only during trip) */}
          {isInTrip && fuelCard && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {t('profile.accountDetails.fuelCard')}
                </CardTitle>
                <CardDescription>
                  {t('profile.accountDetails.fuelCardDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {fuelCard.cardNumber && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t('profile.accountDetails.cardNumber')}
                      </span>
                      <span className="font-mono">{fuelCard.cardNumber}</span>
                    </div>
                  )}
                  {fuelCard.pin && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t('profile.accountDetails.pin')}
                      </span>
                      <span className="font-mono font-bold text-lg">
                        {fuelCard.pin}
                      </span>
                    </div>
                  )}
                  {fuelCard.fourDigitPin && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t('profile.accountDetails.fourDigitPin')}
                      </span>
                      <span className="font-mono">{fuelCard.fourDigitPin}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Trip History */}
          {!isLoading && account && !isNotFound && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="h-5 w-5" />
                  {t('profile.tripHistory.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isTripHistoryLoading && allTrips.length === 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t('profile.tripHistory.loading')}</span>
                  </div>
                )}
                {allTrips.length === 0 &&
                  !isTripHistoryLoading && (
                    <p className="text-sm text-muted-foreground">
                      {t('profile.tripHistory.empty')}
                    </p>
                  )}
                {allTrips.length > 0 && (
                  <div className="space-y-3">
                    {allTrips.map((trip, index) => {
                      const startDate = new Date(trip.startDate)
                      const endDate = new Date(trip.endDate)
                      const durationMs =
                        endDate.getTime() - startDate.getTime()
                      const durationMinutes = Math.round(
                        durationMs / 1000 / 60,
                      )
                      const hours = Math.floor(durationMinutes / 60)
                      const mins = durationMinutes % 60
                      const durationStr =
                        hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
                      const isFirstTrip = index === 0
                      const timeFormat: Intl.DateTimeFormatOptions = {
                        hour: '2-digit',
                        minute: '2-digit',
                      }
                      const dateFormat: Intl.DateTimeFormatOptions = {
                        month: 'short',
                        day: 'numeric',
                        year:
                          startDate.getFullYear() !==
                          new Date().getFullYear()
                            ? 'numeric'
                            : undefined,
                      }

                      return (
                        <div
                          key={trip.rentalId}
                          className="rounded-lg border p-3 space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium">
                              {trip.vehicle.model ||
                                `#${localizeDigits(String(trip.vehicle.vehicleNb))}`}
                            </div>
                            <div className="text-sm font-medium text-end">
                              ${localizeDigits(trip.rentalCost.toFixed(2))}{' '}
                              <span className="text-xs text-muted-foreground">
                                {trip.currency}
                              </span>
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground space-y-0.5">
                            <div>
                              {startDate.toLocaleDateString(undefined, dateFormat)}{' '}
                              {startDate.toLocaleTimeString(undefined, timeFormat)}
                              {' → '}
                              {endDate.toLocaleTimeString(undefined, timeFormat)}
                              {' '}
                              &middot; {durationStr}
                            </div>
                            {isFirstTrip && (
                              <div className="text-xs font-medium text-foreground/70">
                                <ElapsedSince date={endDate} />{' '}
                                {t('profile.tripHistory.ago')}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                    {hasMoreTrips && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={loadMoreTrips}
                        disabled={isTripHistoryFetching}
                      >
                        {isTripHistoryFetching ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        {t('profile.tripHistory.loadMore')}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* No Account Found */}
          {isNotFound && !isLoading && (
            <Card>
              <CardContent className="py-8 text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {t('profile.accountDetails.accountNotFound')}
                </p>
                <Button
                  className="mt-4"
                  variant="outline"
                  onClick={() => navigate({ to: '/profile' })}
                >
                  {t('common.goBack')}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </PullToRefresh>

      {/* Start Trip Dialog */}
      <AlertDialog
        open={showStartTripDialog}
        onOpenChange={setShowStartTripDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('profile.accounts.startTripTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('profile.accounts.startTripDescription', {
                name: account?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('settings.account.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleStartTrip}>
              {t('settings.account.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* End Trip Dialog */}
      <AlertDialog open={showEndTripDialog} onOpenChange={setShowEndTripDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('profile.accounts.endTripTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('profile.accounts.endTripDescription', {
                name: account?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('settings.account.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="outline"
              onClick={() => {
                setShowEndTripDialog(false)
                setShowEndTripReserveDialog(true)
              }}
            >
              {t('profile.accounts.endReserve')}
            </AlertDialogAction>
            <AlertDialogAction onClick={handleEndTrip}>
              {t('profile.accounts.endOnly')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* End Trip & Reserve Dialog - Account Selection */}
      <AlertDialog
        open={showEndTripReserveDialog}
        onOpenChange={setShowEndTripReserveDialog}
      >
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('profile.accounts.endTripReserveTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('profile.accounts.endTripReserveDescription', {
                name: account?.name,
                car: account?.tripCar,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto py-1">
            {accounts.map((acc) => (
              <Card
                key={acc.id}
                className="cursor-pointer hover:bg-accent transition-colors ring-1 ring-border"
                onClick={() => handleEndTripAndReserve(acc.id)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{acc.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {acc.email}
                      </p>
                    </div>
                    {acc.status !== 'idle' && (
                      <Badge variant="outline" className="text-xs">
                        {acc.status === 'ongoing-trip'
                          ? t('profile.accounts.tripActive')
                          : t('profile.accounts.reserved')}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('settings.account.cancel')}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Reservation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('profile.accountDetails.cancelReservationTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('profile.accountDetails.cancelReservationDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('settings.account.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelReservation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('profile.accountDetails.confirmCancel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
